import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { useMagazineAnalyticsSettingFormModelMethods } from "./methods";
import { useMagazineAnalyticsUpdateMutation } from "../../repository/mutation/use-magazine-analytics-update-mutation";
import { excludeKeys } from "../../type";
import { magazineAnalyticsSettingInputZodSchema } from "../../validation-schema";
import { serverModelToInputModel } from "./converter/server-model-to-input-model";

import type { ExcludeKeys, MagazineAnalyticsSettingFormModel, ServerModel } from "../../type";
import type { MagazineAnalyticsSettingInputZodSchemaType } from "../../validation-schema";

type Props = {
	serverModel: ServerModel;
};

type ViewModel = MagazineAnalyticsSettingFormModel["ViewModel"];
type InputModel = MagazineAnalyticsSettingFormModel["InputModel"];
type QueryData = MagazineAnalyticsSettingFormModel["QueryData"];
export type MagazineAnalyticsSettingFormModelOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export const useCreateMagazineAnalyticsSettingFormModel = ({ serverModel }: Props) => {
	const inputModel = serverModelToInputModel(serverModel);

	const initialViewModel = inputModelToViewModelConverter<
		InputModel,
		ExcludeKeys,
		QueryData,
		MagazineAnalyticsSettingInputZodSchemaType
	>({
		defaultInput: inputModel,
		initInput: undefined,
		currentInput: inputModel,
		excludeKeys,
		zodSchema: magazineAnalyticsSettingInputZodSchema,
	});

	const [viewModel, setViewModel] = useState<ViewModel>(initialViewModel);

	const onSetViewModel: MagazineAnalyticsSettingFormModelOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			} as ViewModel;

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				MagazineAnalyticsSettingInputZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				zodSchema: magazineAnalyticsSettingInputZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[viewModel, inputModel, setViewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	const { onUpdate: _onUpdate } = useMagazineAnalyticsUpdateMutation({
		viewModel,
		guestPageVisibilitySettings: serverModel.guestPageVisibilitySettings,
	});

	const onUpdate = useCallback(async () => {
		const isSaved = await _onUpdate();
		// 保存に失敗したとき（Google 無料予約リンクの連携先を「検索に表示しない」にしようとしたなど）は、未保存のままにする
		if (isSaved) {
			resetViewModel();
		}
	}, [_onUpdate, resetViewModel]);

	return {
		viewModel, //
		// onSetViewModel,
		...useMagazineAnalyticsSettingFormModelMethods({
			viewModel,
			onSetViewModel,
			savedGuestPageVisibility: serverModel.guestPageVisibilitySettings.magazineVisibility,
		}),
		// resetViewModel,
		onUpdate,
	};
};
